<template>
  <div class="reports">
    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="page-header">
            <button
              @click="editSettings()"
              class="btn btn-indigo btn-lg float-right my-1 mx-1"
              v-if="$route.meta.editSettings && $user.administrator">
              <i class="fa fa-cog fa-fw mr-2"></i> Settings
            </button>
            <router-link
              v-if="$route.meta.reportType !== 'tradesReport' && !$route.meta.client"
              :to="{ path: 'new', append: true }"
              class="btn btn-primary btn-lg float-right my-1 mx-1">
              <i class="far fa-file fa-fw mr-2"></i> New {{ loadedReports.searchForm ? loadedReports.searchForm.singular : 'Report' }}
            </router-link>
            <span v-else-if="$route.meta.reportType === 'tradesReport'">
              <router-link
                :to="{ path: 'new/wo', append: true }"
                class="btn btn-info btn-lg float-right my-1 mx-1">
                <i class="fa fa-wrench fa-fw mr-2"></i> New Work Order
              </router-link>
              <router-link
                :to="{ path: 'new/qr', append: true }"
                class="btn btn-primary btn-lg float-right my-1 mx-1">
                <i class="fa fa-quote-right fa-fw mr-2"></i> New Quote Request
              </router-link>
            </span>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <div class="card mb-5">
            <h4 class="card-header">
              <i class="fa fa-fw fa-calendar"></i> Select Date
            </h4>
            <div class="card-body">
              <calendar-input
                :options="{
                  name: 'date',
                  label: 'Select a Date',
                  noLabel: true,
                  inline: true,
                  highlightDates: true
                }" 
                :selectedDates="loadedActiveDates"
                :loadDates="loadDates"
                :getMonthReports="getMonthReports"
                :goToDate="goToDate"
                ref="datePicker">
              </calendar-input>
            </div>
          </div>
          
        </div>
        <div class="col-md-8">
          <div
            v-if="$route.meta.showSearch"
            @click="toggleSearch()"
            v-show="searchHidden">
            <p class="form-control text-muted fake-input interactive-cursor">
              Search...
              <span class="fa fa-search float-right"></span>
            </p>
          </div>
          <div v-show="!searchHidden">
            <vue-form
                ref="form"
                :sections="reportsSearchForm"
                :options="{
                  submit: 'Search',
                  submitting: 'Searching...',
                  permalink: $route.meta.reportType,
                  method: 'search',
                  fullWidth: true
                }">
              <span slot="title">
                <i class="fa fa-search fa-fw"></i> Search {{ loadedReports.searchForm.plural }}
                <span
                  class="fa fa-times float-right fa-lg text-danger interactive-cursor"
                  @click="toggleSearch()">
                </span>
              </span>
              <div class="col" slot="actions">
                <button class="btn btn-info" type="button" @click="clearSelectedMonth(); search();">
                  <i class="fa fa-search fa-fw"></i> Search
                </button>
              </div>
            </vue-form>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-sm-12">
          <paging-input ref="paging"
          @change="changePage"
          :options="{
            current: (loadedReports.offset + loadedReports.limit) / loadedReports.limit,
            totalResults: loadedReports.count,
            limit: loadedReports.limit
          }"
          v-if="loadedReports.count">
        </paging-input>
          <div v-if="loadedReports.permalink === $route.meta.reportType && loadedReports.rows.length">
            <div v-for="(object, i) in loadedReports.rows" :class="{ 'hover': i % 2 == 0 }" :key="i">
              <component :is="parseType($route.meta.reportType) + '-display'" :object="object">
              </component>
            </div>
          </div>

          <paging-input ref="paging"
            @change="changePage"
            :options="{
              current: (loadedReports.offset + loadedReports.limit) / loadedReports.limit,
              totalResults: loadedReports.count,
              limit: loadedReports.limit
            }"
            v-if="loadedReports.count">
          </paging-input>
          <div class="alert alert-info" v-else>
            <i class="far fa-meh fa-fw fa-lg"></i> No reports were found that matched your criteria
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex'
  import { EDIT_DATABASE_OBJECT, LOAD_REPORTS, LOAD_ACTIVE_DATES} from '@/vuex/types'
  import CalendarInput from '@/components/inputs/CalendarInput'
  import PagingInput from '@/components/inputs/PagingInput'
  import AsbestosReportDisplay from '@/components/cards/AsbestosReport'
  import DefectsReportDisplay from '@/components/cards/DefectsReport'
  import EsmReportDisplay from '@/components/cards/EsmReport'
  import IncidentReportDisplay from '@/components/cards/IncidentReport'
  import InspectionReportDisplay from '@/components/cards/InspectionReport'
  import InsuranceReportDisplay from '@/components/cards/InsuranceReport'
  import OhsReportDisplay from '@/components/cards/OhsReport'
  import PropertyPhotosReportDisplay from '@/components/cards/PropertyPhotosReport'
  import TaxInvoiceRequestDisplay from '@/components/cards/TaxInvoiceRequest'
  import TradesDisplay from '@/components/cards/Trades'
  import VueForm from '@/components/Form'
  export default {
    name: 'Reports',
    components: {
      AsbestosReportDisplay,
      CalendarInput,
      DefectsReportDisplay,
      EsmReportDisplay,
      IncidentReportDisplay,
      InspectionReportDisplay,
      InsuranceReportDisplay,
      PagingInput,
      PropertyPhotosReportDisplay,
      OhsReportDisplay,
      TaxInvoiceRequestDisplay,
      TradesDisplay,
      VueForm
    },
    data () {
      return {
        editFormPermalink: '',
        editFormUuid: '',
        searchForm: [],
        searchOffset: 0,
        searchLimit: 10,
        searchHidden: false,
        newSearch: true,
        noResults: 'Your search returned 0 results',
        pagingOptions: {
          totalResults: 10,
          limit: 10,
          current: 1
        },
        searchIndex: 0,
        selectedMonth: null,
      }
    },
    computed: {
      ...mapGetters([
        'loadedReports',
        'loadedActiveDates',
        'reportsSearchForm',
        '$sidebar'
      ])
    },
    watch: {
      $route () {
        this.createNewSearch()
      },
      },
    methods: {
      async loadDates(options) {
        await this.$store.dispatch(LOAD_ACTIVE_DATES, { ...options, reportType: this.$route.meta.reportType });
      },
      async getMonthReports(options){
        if (this.searchIndex || !this.$route.query.report) {
          this.searchOffset = 0
          try {
              let reportData = {
                reportType: this.$route.meta.reportType,
                reportUuid: this.$route.query.report,
                searchForm: [],
                selectedMonth: options.selectedMonth,
                offset: this.searchOffset,
                limit: this.loadedReports.limit,
                newSearch: this.newSearch
              }
              await this.$store.dispatch(LOAD_REPORTS, reportData);
              this.selectedMonth = options.selectedMonth;              
          } catch (error) {
            console.error("Error loading data:", error);
          }
      }
        this.searchIndex = 0
      },
      
      async createNewSearch () {
        this.searchOffset = 0
        let reportData = {
          reportType: this.$route.meta.reportType,
          reportUuid: this.$route.query.report,
          searchForm: [],
          date: new Date(),
          offset: this.searchOffset,
          limit: this.loadedReports.limit,
          newSearch: true
        }
        // If it is called by MANAGER (client = true) then it should filter the reports based on its companyuuid
        if (this.$route.meta.client) {
          reportData.client = true
        }
        await this.$store.dispatch(LOAD_REPORTS, reportData)
        this.$refs.datePicker.setDate(new Date())
      },
      async search () {
        await this.$refs.form.validateForm()
        if (this.$refs.form.validForm) {
          let searchForm = this.reportsSearchForm
          searchForm.splice(-1, 1)
          let reportData = {
            reportType: this.$route.meta.reportType,
            searchForm,
            ...(this.selectedMonth ? { selectedMonth: this.selectedMonth } : {}),
            offset: this.searchOffset,
            limit: this.loadedReports.limit,
            newSearch: this.newSearch
          }
          // If it is called by MANAGER (client = true) then it should filter the reports based on its companyuuid
          if (this.$route.meta.client) {
            reportData.client = true
          }
          await this.$store.dispatch(LOAD_REPORTS, reportData)
        }
      },
      async goToDate (date) {
        if (this.searchIndex || !this.$route.query.report) {
          this.searchOffset = 0
          let reportData = {
            reportType: this.$route.meta.reportType,
            searchForm: [],
            date: date,
            offset: this.searchOffset,
            limit: this.loadedReports.limit,
            newSearch: true
          }
          // If it is called by MANAGER (client = true) then it should filter the reports based on its companyuuid
          if (this.$route.meta.client) {
            reportData.client = true
          }
          await this.$store.dispatch(LOAD_REPORTS, reportData)
        }
        this.searchIndex = 1
      },
      async toggleSearch () {
        this.searchHidden = !this.searchHidden
        if (!this.searchHidden) {
          await this.$nextTick()
          this.$refs.form.$el.querySelectorAll('input[type=text]')[0].focus()
        }
      },
      async changePage (page) {
        this.searchOffset = (page - 1) * this.loadedReports.limit
        await this.search()
      },
      async clearSelectedMonth () {
        this.selectedMonth = null;
      },

      async editSettings () {
        await this.$store.dispatch(EDIT_DATABASE_OBJECT, {
          permalink: 'editAppSettings',
          uuid: this.$sidebar.currentLink.uuid
        })
      },
      parseType (type) {
        switch (type) {
          case 'taxInvoiceRequest':
            return 'tax-invoice-request'
          case 'incidentReport':
            return 'incident-report'
          case 'inspectionReport':
            return 'inspection-report'
          case 'propertyPhotosReport':
            return 'property-photos-report'
          case 'asbestosReport':
            return 'asbestos-report'
          case 'ohsReport':
            return 'ohs-report'
          case 'esmReport':
            return 'esm-report'
          case 'defectsReport':
            return 'defects-report'
          case 'insuranceReport':
            return 'insurance-report'
          case 'tradesReport':
            return 'trades'
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/admin/Reports.vue